import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useImageOptimizationContext } from 'ui/contexts/ImageOptimizationContext';
import { GetImageUrl, GetSrcSetString } from '../../../../../helpers/getImageUrl';
import config from '../../../../../helpers/imageSizes';
import { inView } from '../../../../../helpers/inView';
import styles from './Picture.module.scss';

export interface PictureProps {
	url: string;
	altText: string;
	focalPoint?: { left: number; top: number };
	width?: number;
	height?: number;
	sizes?: string;
	lazyload?: boolean;
	imageRatio?: number; // for image's src
	className?: string;
	backgroundColor?: string;
	imageCropping?: 'cover' | 'contain';
	fetchpriority?: 'high' | 'low' | 'auto';
}

export const UI_Picture = ({
	sizes = '100vw',
	url,
	altText,
	lazyload = true,
	focalPoint,
	width,
	height,
	imageRatio,
	className,
	backgroundColor,
	imageCropping = 'cover',
	fetchpriority = 'auto',
}: PictureProps) => {
	const ImageOptimization = useImageOptimizationContext();
	const { thumbnailSize, fallbackSize } = config.images;
	const thumbnailImageUrl = GetImageUrl({ url, width: thumbnailSize });

	const [src, setSrc] = useState(thumbnailImageUrl);
	const [imgSrcSet, setImgSrcSet] = useState(thumbnailImageUrl);
	const focalPointToCSS = focalPoint ? focalPoint.left * 100 + '% ' + focalPoint.top * 100 + '%' : '50% 50%';

	const pictureRef = useRef(null);

	const loading = lazyload === false ? 'eager' : 'lazy';
	const priority = fetchpriority === 'high' ? true : false;

	useEffect(() => {
		if (lazyload) inView({ elm: pictureRef.current, callback: reveal });
		if (!lazyload) reveal();
	});

	const reveal = () => {
		const srcSetString = GetSrcSetString({ url, focalPoint, aspectRatio: imageRatio });
		const srcString = GetImageUrl({ url, width: fallbackSize });

		setImgSrcSet(srcSetString);
		setSrc(srcString);
	};

	return (
		<picture
			ref={pictureRef}
			className={classNames(styles.Picture, imageCropping === 'contain' ? styles.Picture___contain : styles.Picture___cover, className)}
			style={{
				backgroundColor: backgroundColor || null,
			}}
		>
			<ImageOptimization
				className={classNames(styles.Picture_asset)}
				src={src}
				width={width ?? fallbackSize}
				height={height ?? fallbackSize * (imageRatio ?? 1)}
				alt={altText ?? ''}
				srcSet={imgSrcSet}
				sizes={sizes}
				style={{ objectPosition: imageCropping === 'contain' ? 'initial' : focalPointToCSS }}
				fetchpriority={fetchpriority}
				loading={loading}
				priority={priority}
			/>
		</picture>
	);
};
